import { computed, ref } from "vue";
import { useStore } from 'vuex';

export function usePlans() {
    const store = useStore();
    const spinner = ref(false);
    const showDeleteConfirm = ref(false);
    const selectedPlanId = ref(null);
    const alertTable = ref({ state: false, message: '', color: 'success' });

    const getPlans = async () => {
        spinner.value = true;
        try {
            await store.dispatch('appPlans/getPlansAction');
        } catch (error) {
            showAlert('Error al cargar planes', 'danger');
        } finally {
            spinner.value = false;
        }
    };

    const getPlanDetail = async (planId) => {
        spinner.value = true;
        try {
            const data = await store.dispatch('appPlans/getPlanDetailAction', planId);
            return data;
        } catch (error) {
            showAlert('Error al cargar detalle del plan', 'danger');
            return null;
        } finally {
            spinner.value = false;
        }
    };

    const createPlan = async (planData) => {
        spinner.value = true;
        try {
            await store.dispatch('appPlans/createPlanAction', planData);
            showAlert('Plan creado exitosamente', 'success');
            return { success: true };
        } catch (error) {
            showAlert(error.response?.data?.message || 'Error al crear plan', 'danger');
            return { success: false };
        } finally {
            spinner.value = false;
        }
    };

    const updatePlan = async (planId, planData) => {
        spinner.value = true;
        try {
            await store.dispatch('appPlans/updatePlanAction', { planId, data: planData });
            showAlert('Plan actualizado exitosamente', 'success');
            return { success: true };
        } catch (error) {
            showAlert(error.response?.data?.message || 'Error al actualizar plan', 'danger');
            return { success: false };
        } finally {
            spinner.value = false;
        }
    };

    const deletePlan = async (planId) => {
        spinner.value = true;
        try {
            await store.dispatch('appPlans/deletePlanAction', planId);
            showAlert('Plan eliminado exitosamente', 'success');
            return { success: true };
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al eliminar plan';
            showAlert(msg, 'danger');
            return { success: false, message: msg };
        } finally {
            spinner.value = false;
        }
    };

    const confirmDelete = (planId) => {
        selectedPlanId.value = planId;
        showDeleteConfirm.value = true;
    };

    const executeDelete = async () => {
        const planId = selectedPlanId.value;
        if (planId) {
            const result = await deletePlan(planId);
            if (result.success) {
                closeDeleteConfirm();
                await getPlans();
            } else {
                closeDeleteConfirm();
            }
        }
    };

    const closeDeleteConfirm = () => {
        showDeleteConfirm.value = false;
        selectedPlanId.value = null;
    };

    const showAlert = (message, color = 'success') => {
        alertTable.value = { state: true, message, color };
        setTimeout(() => {
            alertTable.value.state = false;
        }, 5000);
    };

    const formatPrice = (price) => {
        if (!price || price === '0') return 'Gratis';
        const num = parseInt(price);
        return '$' + num.toLocaleString('es-CO');
    };

    const plans = computed(() => store.state.appPlans.plans);
    const planDetail = computed(() => store.state.appPlans.planDetail);

    return {
        spinner,
        showDeleteConfirm,
        selectedPlanId,
        alertTable,
        plans,
        planDetail,
        getPlans,
        getPlanDetail,
        createPlan,
        updatePlan,
        deletePlan,
        confirmDelete,
        executeDelete,
        closeDeleteConfirm,
        showAlert,
        formatPrice,
    };
}
